import React, { useEffect, useRef, useState } from "react";
import { FaEdit } from "react-icons/fa";
import {
  FormField,
  PrimaryButton,
  SecondaryButton,
} from "../../../components/UI/DesignStandards";
import {
  TEAM_NAME_MAX_LENGTH,
  normalizedTeamName,
  teamNameError,
} from "../hooks/teamName";

const DetailRow = ({ label, value }) => (
  <div>
    <div className="text-xs font-medium text-brand-muted mb-1">{label}</div>
    <div className="text-sm text-brand-charcoal">{value}</div>
  </div>
);

const smallLineupNote = (lineupSize) => {
  const count = Number(lineupSize);
  if (!count || count > 2) return null;
  return "Singles and doubles are listed by bowler name on scoring and results. This name still shows on the team list.";
};

const TeamNameCard = ({ teamName = "", lineupSize = 0, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(teamName);
  const [fieldError, setFieldError] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const note = smallLineupNote(lineupSize);

  useEffect(() => {
    if (!isEditing) setDraft(teamName || "");
  }, [teamName, isEditing]);

  useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);

  const handleStartEdit = () => {
    setDraft(teamName || "");
    setFieldError("");
    setIsEditing(true);
  };

  const handleCancel = () => {
    setDraft(teamName || "");
    setFieldError("");
    setIsEditing(false);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    const message = teamNameError(draft);
    if (message) {
      setFieldError(message);
      return;
    }
    const nextName = normalizedTeamName(draft);
    if (!nextName || nextName === (teamName || "")) {
      setFieldError("");
      setIsEditing(false);
      return;
    }

    setSaving(true);
    try {
      await onSave(nextName);
      setFieldError("");
      setIsEditing(false);
    } catch (error) {
      if (error?.code === "INVALID_TEAM_NAME" && error.message) {
        setFieldError(error.message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-inset p-4 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="heading-display text-lg">Team name</h2>
        </div>
        {!isEditing ? (
          <SecondaryButton
            type="button"
            onClick={handleStartEdit}
            icon={FaEdit}
            className="!px-3 !py-2 text-sm flex-shrink-0"
            aria-label="Edit team name"
          >
            Edit
          </SecondaryButton>
        ) : null}
      </div>

      {isEditing ? (
        <form onSubmit={handleSave} className="space-y-4">
          <FormField
            ref={inputRef}
            label="Team name"
            type="text"
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
              if (fieldError) setFieldError("");
            }}
            error={fieldError}
            placeholder="Team name"
            required
            maxLength={TEAM_NAME_MAX_LENGTH}
            autoComplete="off"
            enterKeyHint="done"
            aria-label="Team name"
            aria-invalid={fieldError ? "true" : "false"}
          />
          <p className="text-sm text-brand-muted -mt-2">
            Letters, numbers, spaces, hyphens, apostrophes, periods, or
            underscores. {TEAM_NAME_MAX_LENGTH} characters max.
          </p>
          <div className="flex flex-wrap gap-2">
            <PrimaryButton
              type="submit"
              loading={saving}
              aria-label="Save team name"
            >
              Save
            </PrimaryButton>
            <SecondaryButton
              type="button"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </SecondaryButton>
          </div>
        </form>
      ) : (
        <DetailRow label="Team name" value={teamName || "No team name"} />
      )}

      {note ? <p className="text-sm text-brand-muted">{note}</p> : null}
    </div>
  );
};

export default TeamNameCard;
